import * as React from 'react';
import { connect } from 'react-redux';

import { ModalFrame, ModalFrameHeader } from 'components/modal';

import ShippingStepBusinessDetailsMissing from '../ShippingStepBusinessDetailsMissing';
import ShippingStepDetailsForm from '../ShippingStepDetailsForm';
import ShippingStepPhoneForm from '../ShippingStepPhoneForm';
import ShippingStepSettingsForm from '../ShippingStepSettingsForm';

import { saveSubcampaignShipping } from '../../reducers/shipping';
import { subcampaignShippingEdited } from 'modules/subcampaigns/reducers/subcampaign';
import { danger, success } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';

import type {
  OrganizationSettings,
  ShippingAddress
} from 'types';

applyTranslations(require('./edit-shipping-modal.i18n.json'));

export const MODE_BANK_DETAILS = 'bank-details';
export const MODE_BUSINESS_DETAILS_ADD = 'bank-details-add';
export const MODE_BUSINESS_DETAILS = 'business-details';
export const MODE_EDIT_PHONE = 'phone';
export const MODE_EDIT_ADDRESS = 'address';

const STEP_MIN_HEIGHT = 440;

type DefaultProps = {
  shippingAddress: ShippingAddress
};

type OwnProps = DefaultProps & {
  subcampaignId: number,
  mode: 'phone' | 'address' | 'business-details' | 'bank-details' | 'bank-details-add',
  isEditCampaignAddress?: boolean,
  onCloseModal: () => void
};

type StateProps = {
  settingsId: number,
  organizationSettings: OrganizationSettings
};

type DispatchProps = {
  dispatchSuccess: (a: string) => Promise<void>,
  dispatchDanger: (a: string) => Promise<void>,
  dispatchShippingAddressEdited: (a: ShippingAddress) => Promise<void>,
  dispatchSaveSubcampaignShipping: (b: number, a: ShippingAddress) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  detailsAreMissing: boolean
};

class EditShippingModal extends React.PureComponent<Props, State> {
  state = {
    detailsAreMissing: false
  };

  static defaultProps: DefaultProps = {
    shippingAddress: {
      address1: '',
      address2: '',
      city: '',
      country: '',
      recipient: '',
      stateProvinceRegion: '',
      zip: ''
    }
  };

  handleChangeAddress = (address?: ShippingAddress | null): void => {
    const { shippingAddress } = this.props;

    this.handleSave({
      ...shippingAddress,
      ...address
    });
  };

  handleChangePhone = (phone: string): void => {
    const { shippingAddress } = this.props;

    this.handleSave({
      ...shippingAddress,
      phone
    });
  };

  handleSave = (shippingAddress: ShippingAddress): void => {
    const {
      subcampaignId,
      onCloseModal,
      dispatchSuccess,
      dispatchDanger,
      dispatchShippingAddressEdited,
      dispatchSaveSubcampaignShipping
    } = this.props;

    dispatchSaveSubcampaignShipping(subcampaignId, shippingAddress)
      .then(() => {
        dispatchShippingAddressEdited(shippingAddress);
        dispatchSuccess(__t('edit_shipping_modal.message_success'));
      })
      .catch(() => dispatchDanger(__t('edit_shipping_modal.message_failed')));

    onCloseModal();
  };

  handleNoDetailsProvided = () => {
    this.setState({
      detailsAreMissing: true
    });
  };

  render(): React.ReactNode {
    const {
      mode,
      organizationSettings,
      shippingAddress,
      subcampaignId,
      settingsId,
      isEditCampaignAddress,
      onCloseModal
    } = this.props;

    const { detailsAreMissing } = this.state;

    let componentStep: React.ReactNode = null;

    switch (mode) {
      case MODE_EDIT_ADDRESS:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={isEditCampaignAddress ? __t('edit_shipping_modal.title_address_with_email') : __t('edit_shipping_modal.title_address')}

            />
            <ShippingStepDetailsForm
              allowDelete={false}
              submitButtonColor="primary"
              address={shippingAddress}
              organizationSettings={organizationSettings}
              title={__t('edit_shipping_modal.subtitle_address')}
              onCancel={onCloseModal}
              onSave={this.handleChangeAddress}
              isEditCampaignAddress={isEditCampaignAddress}
            />
          </React.Fragment>
        );
        break;
      case MODE_EDIT_PHONE:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('edit_shipping_modal.title_phone')}
            />
            <ShippingStepPhoneForm
              title={__t('edit_shipping_modal.subtitle_phone')}
              submitButtonColor="primary"
              minHeight={STEP_MIN_HEIGHT}
              phone={shippingAddress && shippingAddress.phone || ''}
              onCancel={onCloseModal}
              onChangePhone={this.handleChangePhone}
            />
          </React.Fragment>
        );
        break;
      case MODE_BUSINESS_DETAILS:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('edit_shipping_modal.title_business_details_edit')}
            />
            <ShippingStepSettingsForm
              key="businessDetails"
              forceEditMode
              minHeight={STEP_MIN_HEIGHT}
              settingsId={settingsId}
              subcampaignId={subcampaignId}
              organizationSettings={organizationSettings}
              settingsGroup="businessDetails"
              submitButtonColor="primary"
              onCancel={onCloseModal}
              onSave={onCloseModal}
            />
          </React.Fragment>
        );
        break;
      case MODE_BUSINESS_DETAILS_ADD:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('edit_shipping_modal.title_business_details_add')}
            />
            {
              detailsAreMissing ?
                <ShippingStepBusinessDetailsMissing
                  minHeight={STEP_MIN_HEIGHT}
                  subcampaignId={subcampaignId}
                  settingsId={settingsId}
                  organizationSettings={organizationSettings}
                  buttonText={__t('edit_shipping_modal.button_okay')}
                  onCancel={onCloseModal}
                  onClose={onCloseModal}
                /> :
                <ShippingStepSettingsForm
                  key="businessDetails"
                  forceEditMode
                  minHeight={STEP_MIN_HEIGHT}
                  settingsId={settingsId}
                  subcampaignId={subcampaignId}
                  organizationSettings={organizationSettings}
                  settingsGroup="businessDetails"
                  submitButtonColor="primary"
                  onCancel={onCloseModal}
                  onSave={onCloseModal}
                  onContinue={this.handleNoDetailsProvided}
                />
            }
          </React.Fragment>
        );
        break;
      case MODE_BANK_DETAILS:
        componentStep = (
          <React.Fragment>
            <ModalFrameHeader
              title={__t('edit_shipping_modal.title_bank_details')}
            />
            <ShippingStepSettingsForm
              key="bankDetails"
              forceEditMode
              minHeight={STEP_MIN_HEIGHT}
              settingsId={settingsId}
              subcampaignId={subcampaignId}
              organizationSettings={organizationSettings}
              settingsGroup="bankDetails"
              submitButtonColor="primary"
              onCancel={onCloseModal}
              onSave={onCloseModal}
              onContinue={onCloseModal}
            />
          </React.Fragment>
        );
        break;
      default:
        break;
    }

    return (
      <section
        className="edit-shipping-modal"
        data-qa="edit-shipping-modal"
      >
        <ModalFrame>
          {componentStep}
        </ModalFrame>
      </section>
    );
  }
}

const mapStateToProps = ({ organizations, organizationSettings }): StateProps => {
  const currentOrganization = organizations.currentOrganization;

  return {
    organizationSettings: organizationSettings.organization,
    settingsId: currentOrganization?.agencyOrganizationId || currentOrganization?.id
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveSubcampaignShipping: (subcampaignId: number, address: ShippingAddress): Promise<void> => dispatch(saveSubcampaignShipping(subcampaignId, address)),
  dispatchShippingAddressEdited: (shipping: ShippingAddress): Promise<void> => dispatch(subcampaignShippingEdited(shipping)),
  dispatchSuccess: (message: string) => dispatch(success(message)),
  dispatchDanger: (message: string) => dispatch(danger(message))
});

export default connect(mapStateToProps, mapDispatchToProps)(EditShippingModal);
